import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import config from 'config/config';
import Modal from 'utils/components/modal';
import useSpotConfig from '@host-panel/request/config/use-config';
import request from '@host-panel/request';
import TitleAndDescription, { HEADER_FONTS } from '../../common/description-component';
import GenericFaq from '../../common/faqs/generic-faq';
import HeroImage from '../../common/hero-image';
import SpacerVertical from '../../common/spacer-vertical';
import SelectInput from '../../common/inputs/select-input';
import EmbedCode from '../../common/embed-code';
import Switch from '../../common/inputs/customs/switch';
import ConfirmModal from '../../modals/confirm';
import setReactionsConfig from './config/index';
import ThemeModal from './ThemeModal';
import { getApiGatewayBase } from './helpers/get-api-url';
import ThemePreview from './ThemePreview';

// TODO: remove this after backend sends fresh reactions after PUT request
const delay = () => new Promise(res => setTimeout(res, 100));

const REACTIONS_KEY = 'tenant.reactions.enabled';
const IS_ACTIVE_MAPPER = {
  on: true,
  off: false
};
const CODE_HELP_OPTIONS = [
  {
    value: 'DEFAULT_LOCATION',
    text: 'Above Conversation'
  },
  {
    value: 'ARBITRARY_LOCATION',
    text: 'Manual Code Integration'
  },
  {
    value: 'SIDEBAR_LOCATION',
    text: 'In a Sidebar'
  }
];

const getDefaultTheme = settings => ({
  uuid: 'default',
  name: 'Default',
  title: settings.title,
  enabled: true,
  implementation_method: 'keywords',
  reactions: settings.reactions
});

const getNewTheme = settings => ({
  uuid: null,
  name: '',
  title: null,
  enabled: true,
  implementation_method: 'keywords',
  keywords: '',
  reactions: (settings.reactions || []).map(r => Object.assign({}, r))
});

const isDefaultTheme = themeId => themeId === 'default';

const getOrderedThemesKeys = themes => {
  if (!themes) {
    return [];
  }

  const compareByName = (firstId, secondId) =>
    themes[firstId]?.name.localeCompare(themes[secondId]?.name);
  const allExceptDefault = Object.keys(themes)
    .sort(compareByName)
    .filter(themeId => !isDefaultTheme(themeId));
  return ['default'].concat(allExceptDefault);
};

const toFrontendTheme = theme => ({ ...theme, keywords: theme.keywords.join(',') });

const toBackendThemes = themes =>
  Object.values(themes)
    .filter(({ uuid }) => !isDefaultTheme(uuid))
    .map(theme => {
      const nextTheme = { ...theme, keywords: theme.keywords.split(',').map(k => k.trim()) };

      if (nextTheme.isNewTheme) {
        delete nextTheme.isNewTheme;
        delete nextTheme.uuid;
      }

      return nextTheme;
    });

const Reactions = ({ spotId }) => {
  const [deletedThemes, setDeletedThemes] = useState([]);
  const [shouldHydrate, setShouldHydrate] = useState(true);
  const [settings, setSettings] = useState({});
  const [reactionsEnabled, setReactionsEnabled] = useState(null);
  const [message, setMessage] = useState(null);
  const [[enabled], setConfig, { loading }] = useSpotConfig([REACTIONS_KEY]);
  const [isSettingsDirty, setIsSettingsDirty] = useState(false);
  const [codeHelpMode, setCodeHelpMode] = useState('DEFAULT_LOCATION');
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const [pendingThemeToDelete, setPendingThemeToDelete] = useState(null);
  const themeModalRef = useRef();
  const confirmModalRef = useRef();

  const openThemeModal = themeId => {
    setSelectedThemeId(themeId);
    themeModalRef.current.show();
  };
  const closeThemeModal = () => {
    setSelectedThemeId(null);
    themeModalRef.current.hide();
  };

  const openConfirmModal = themeId => {
    setPendingThemeToDelete(themeId);
    confirmModalRef.current.show();
  };

  const closeConfirmModal = () => {
    setPendingThemeToDelete(null);
    confirmModalRef.current.hide();
  };

  useEffect(() => {
    if (!shouldHydrate) {
      return;
    }
    request('/host-panel/reactions/spot_config', {}).then(res => {
      if (!res) {
        return;
      }

      res.themes = res.themes.reduce((acc, currTheme) => {
        acc[currTheme.uuid] = toFrontendTheme(currTheme);
        return acc;
      }, {});
      res.themes.default = getDefaultTheme(res);
      setSettings(res);
      setShouldHydrate(false);
    });
  }, [shouldHydrate]);

  useEffect(() => {
    if (!loading) {
      setReactionsEnabled(enabled);
    }
  }, [loading]);

  const saveSettings = () => {
    const payload = {
      deleted_themes: deletedThemes,
      themes: toBackendThemes(settings.themes),
      reactions: settings.themes?.default?.reactions,
      mode: 'off',
      title: settings.themes?.default?.title || null,
      is_active: IS_ACTIVE_MAPPER[settings.mode]
    };

    Promise.all([
      setReactionsConfig(payload).then(delay()),
      setConfig({ key: REACTIONS_KEY, value: reactionsEnabled })
    ]).then(() => {
      setMessage(`Settings successfully saved`);
      setShouldHydrate(true);
      setDeletedThemes([]);
      setTimeout(() => {
        setMessage(null);
      }, 5000);
    });
  };

  const settingsChanged = newSettingsExtend => {
    setIsSettingsDirty(true);
    const updatedSettings = Object.assign({}, settings, newSettingsExtend);
    setSettings(updatedSettings);
  };

  const onFeatureModeChange = nextIsEnabled => {
    setReactionsEnabled(nextIsEnabled);
  };

  const isSaveDisabled = useMemo(() => {
    if (!reactionsEnabled) {
      // no need for validations if feature has been deactivated
      return false;
    }

    return !isSettingsDirty;
  }, [settings, isSettingsDirty]);

  const activateTheme = themeId => {
    settings.themes[themeId].enabled = true;
    settingsChanged({ themes: settings.themes });
  };

  const toggleThemeActiveStatus = themeId => {
    settings.themes[themeId].enabled = !settings.themes[themeId].enabled;
    settingsChanged({ themes: settings.themes });
  };

  const deleteTheme = themeId => {
    delete settings.themes[themeId];
    setDeletedThemes(prevDeletedThemes => [...prevDeletedThemes, themeId]);
    settingsChanged({ themes: settings.themes });
  };

  const themeChanged = useCallback(
    (themeId, newThemeSettings) => {
      const nextThemes = { ...settings.themes };
      nextThemes[themeId] = { ...nextThemes[themeId], ...newThemeSettings };
      settingsChanged({ themes: nextThemes });
    },
    [settings]
  );

  const selectedTheme = selectedThemeId && settings.themes?.[selectedThemeId];

  const getIsThemeNameTaken = (themeName, themeId) => {
    return Object.values(settings.themes || {})
      .filter(theme => theme.uuid !== themeId)
      .map(theme => theme.name)
      .includes(themeName);
  };

  return (
    <React.Fragment>
      {message && (
        <div className="success-message">
          <div className="text">{message}</div>
        </div>
      )}
      <div className="articleReactions">
        <section className="main-section">
          <SpacerVertical size="40px" />
          <TitleAndDescription
            titleFont={HEADER_FONTS.Large}
            title="Reactions"
            description={
              <React.Fragment>
                <p>
                  The Article Reactions feature is a fun and easy way to engage users with your
                  content. Reactions give you a better understanding of how your community perceives
                  the conversation. You can choose to display the feature on all articles or per
                  article, before the conversation (default) or anywhere on the page (&lt;div&gt;).
                  You can also change the title of each reaction, choose how many you wish to
                  display, and more.
                </p>
              </React.Fragment>
            }
          />

          <SpacerVertical size="20px" />

          <HeroImage
            src="https://static-cdn.spot.im/production/reactions/images/article-reactions_hero2.png"
            alt="Article Reactions preview"
          />

          <TitleAndDescription title="Settings" />

          <SpacerVertical size="-20px" />

          <Switch
            title="Enable/Disable Reactions"
            description="The Reactions unit will be active on all articles."
            value={Boolean(reactionsEnabled)}
            onChange={onFeatureModeChange}
          />

          <SelectInput
            title="Choose Reactions Location"
            onChange={option => setCodeHelpMode(option.value)}
            options={CODE_HELP_OPTIONS}
            value={CODE_HELP_OPTIONS.find(option => option.value === codeHelpMode)}
          />

          {(codeHelpMode === 'SIDEBAR_LOCATION' || codeHelpMode === 'ARBITRARY_LOCATION') && (
            <div>
              <EmbedCode
                type={codeHelpMode === 'ARBITRARY_LOCATION' ? 'reactions' : 'reactions-sidebar'}
                spotId={spotId}
                title="Code for Your Site"
                description="Embed this code where you'd like Reactions to appear."
              />

              <p className="embed-code-following-description">
                Please replace <span className="head-tag">&nbsp;&quot;POST_ID&quot;&nbsp;</span>
                with the relevant post ID.
              </p>
            </div>
          )}

          <div className="settings-seperator" />

          <div className={`settings-container ${reactionsEnabled ? '' : 'is-disabled'}`}>
            <div className="reactions-theme-header-container">
              <TitleAndDescription title="Reactions Themes" />
              <button
                className="reaction-new-theme-button button-small"
                onClick={() => openThemeModal(null)}
                disabled={false}
              >
                Create New Theme
              </button>
              <Modal ref={themeModalRef} onConfirm={null}>
                <ThemeModal
                  spotId={spotId}
                  minReactions={settings.min_reactions}
                  maxReactions={settings.max_reactions}
                  selectedTheme={selectedTheme || getNewTheme(settings)}
                  isDefault={isDefaultTheme(selectedThemeId)}
                  themeChanged={themeChanged}
                  activateTheme={() => activateTheme(selectedThemeId)}
                  onClose={closeThemeModal}
                  getIsThemeNameTaken={getIsThemeNameTaken}
                />
              </Modal>
            </div>

            <SpacerVertical size="20px" />

            <div className="reactions-themes-preview-list-container">
              {getOrderedThemesKeys(settings.themes).map(themeId => (
                <ThemePreview
                  key={themeId}
                  spotId={spotId}
                  theme={settings.themes[themeId]}
                  isDefault={isDefaultTheme(themeId)}
                  onEditClick={() => openThemeModal(themeId)}
                  onDeleteClick={() => openConfirmModal(themeId)}
                  onToggleActiveStatus={() => {
                    toggleThemeActiveStatus(themeId);
                  }}
                />
              ))}
              <Modal ref={confirmModalRef} className="confirm-modal">
                <ConfirmModal
                  title="Confirm Action"
                  onClose={() => {
                    closeConfirmModal();
                  }}
                  onConfirm={() => {
                    deleteTheme(pendingThemeToDelete);
                    closeConfirmModal();
                  }}
                  description="This theme will be deleted. Are you sure?"
                  buttonName="Delete"
                />
              </Modal>
            </div>
          </div>

          <SpacerVertical size="40px" />

          <button className="policy-button" onClick={saveSettings} disabled={isSaveDisabled}>
            Save & Publish
          </button>

          <SpacerVertical size="40px" />
        </section>

        <GenericFaq>
          <div className="faq">Where will Reactions be displayed once I enable the unit?</div>
          <div className="faq-description">
            Once you enable the feature it will be displayed above the Conversation by default. The
            location of the feature can be changed manually by using &lt;div&gt; under the “Manually
            Code Integration” section on this page.
          </div>

          <div className="faq">How can I edit reactions?</div>
          <div className="faq-description">
            On this page, below the Title, you can find all the Reactions icons and their titles.
            You can control what icons you like to display and can edit their titles. Once you made
            changes, they will be applied to all articles (new and old).
          </div>

          <div className="faq">How can I remove Reactions?</div>
          <div className="faq-description">
            You can disable this feature by choosing the “Disable Feature” option in the “Enable
            Articles Reactions” dropdown.
          </div>

          <div className="faq">Still have questions?</div>
          <div className="faq-description">
            <span>
              Visit our
              <a
                className="faq-link"
                href={config.api.urls.support}
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                support{' '}
              </a>
              site or go ahead and
              <a
                className="faq-link"
                href={config.api.urls.contact}
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                contact{' '}
              </a>
              our support team.
            </span>
          </div>
        </GenericFaq>
      </div>
    </React.Fragment>
  );
};

Reactions.propTypes = {
  spotId: PropTypes.string
};

export default Reactions;
